import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  LogIn, 
  FileText, 
  Clock, 
  CheckCircle, 
  Plus,
  Calendar,
  Languages,
  DollarSign,
  Download,
  LogOut,
  Send,
  FolderOpen,
  File,
  Receipt,
  ExternalLink,
  Upload,
  Loader2,
  Paperclip
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [client, setClient] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [documents, setDocuments] = useState({ client_documents: [], work_order_documents: [] });
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [activeTab, setActiveTab] = useState('orders');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    work_order_type: 'interpreting',
    languages: '',
    date: '',
    time: '',
    duration: '1',
    location: '',
    notes: ''
  });
  
  // File upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedWorkOrderForUpload, setSelectedWorkOrderForUpload] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: 'source',
    notes: ''
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const clientData = localStorage.getItem('client_portal_user');
    if (clientData) {
      const parsed = JSON.parse(clientData);
      setClient(parsed);
      setIsLoggedIn(true);
      fetchClientData(parsed.client_id);
    }
  }, []);

  const fetchClientData = async (clientId) => {
    setLoading(true);
    try {
      // Fetch work orders and invoices
      const dataRes = await fetch(`${API_URL}/api/client-portal/data/${clientId}`);
      if (dataRes.ok) {
        const data = await dataRes.json();
        setWorkOrders(data.work_orders || []);
        setInvoices(data.invoices || []);
      }
      
      // Fetch documents
      const docsRes = await fetch(`${API_URL}/api/client-portal/documents/${clientId}`);
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/client-portal/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setClient(data.client);
        setIsLoggedIn(true);
        localStorage.setItem('client_portal_user', JSON.stringify(data.client));
        fetchClientData(data.client.client_id);
        toast.success('Welcome back!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_portal_user');
    setIsLoggedIn(false);
    setClient(null);
    setWorkOrders([]);
    setInvoices([]);
    setDocuments({ client_documents: [], work_order_documents: [] });
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/client-portal/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: client.client_id,
          ...requestForm,
          languages: requestForm.languages.split(',').map(l => l.trim())
        })
      });
      
      if (res.ok) {
        toast.success('Request submitted! We will contact you shortly.');
        setShowRequestForm(false);
        setRequestForm({
          work_order_type: 'interpreting',
          languages: '',
          date: '',
          time: '',
          duration: '1',
          location: '',
          notes: ''
        });
        fetchClientData(client.client_id);
      } else {
        toast.error('Failed to submit request');
      }
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const openUploadDialog = (workOrder = null) => {
    setSelectedWorkOrderForUpload(workOrder);
    setUploadForm({ category: 'source', notes: '' });
    setUploadDialogOpen(true);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);

    try {
      // Convert file to base64 data URL (for demo - in production use cloud storage)
      const reader = new FileReader();
      reader.onload = async () => {
        const fileUrl = reader.result;
        
        const uploadData = {
          client_id: client.client_id,
          work_order_id: selectedWorkOrderForUpload?.work_order_id || null,
          filename: file.name,
          file_url: fileUrl,
          file_size: file.size,
          mime_type: file.type,
          category: uploadForm.category,
          notes: uploadForm.notes || null
        };

        const res = await fetch(`${API_URL}/api/client-portal/upload-file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(uploadData)
        });

        if (res.ok) {
          const data = await res.json();
          toast.success(data.message);
          setUploadDialogOpen(false);
          fetchClientData(client.client_id);
        } else {
          const error = await res.json();
          toast.error(error.detail || 'Failed to upload file');
        }
        setUploadingFile(false);
      };

      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploadingFile(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
      setUploadingFile(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new_request: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-green-100 text-green-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.replace(/_/g, ' ')}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext)) return 'text-red-500';
    if (['doc', 'docx'].includes(ext)) return 'text-blue-500';
    if (['xls', 'xlsx'].includes(ext)) return 'text-green-500';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'text-purple-500';
    return 'text-gray-500';
  };

  // Total documents count
  const totalDocs = documents.client_documents.length + documents.work_order_documents.length;

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
              alt="Bylyngo"
              className="h-20 w-auto mx-auto mb-4"
            />
            <CardTitle className="text-2xl">Client Portal</CardTitle>
            <p className="text-muted-foreground">Sign in to manage your language services</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="your@company.com"
                  required
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="client-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="client-login-btn">
                <LogIn className="h-4 w-4 mr-2" />
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Need an account? Contact us at info@bylyngo.com
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
              alt="Bylyngo"
              className="h-10 w-auto"
            />
            <div>
              <h1 className="font-semibold text-lg">Client Portal</h1>
              <p className="text-sm text-muted-foreground">{client?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openUploadDialog(null)} data-testid="upload-document-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setShowRequestForm(true)} data-testid="request-service-btn">
              <Plus className="h-4 w-4 mr-2" />
              Request Service
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="client-logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{workOrders.length}</p>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{workOrders.filter(w => ['new_request', 'confirmed', 'assigned', 'in_progress'].includes(w.status)).length}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{workOrders.filter(w => ['completed', 'delivered', 'closed'].includes(w.status)).length}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{invoices.filter(i => i.status === 'sent').length}</p>
                  <p className="text-sm text-muted-foreground">Pending Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <FolderOpen className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{totalDocs}</p>
                  <p className="text-sm text-muted-foreground">Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Request Form Modal */}
        {showRequestForm && (
          <Card className="border-2 border-orange-200">
            <CardHeader className="bg-orange-50">
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-orange-600" />
                Request Language Service
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleRequestSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select value={requestForm.work_order_type} onValueChange={(v) => setRequestForm({...requestForm, work_order_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="interpreting">Interpreting</SelectItem>
                        <SelectItem value="translation">Translation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Languages (comma separated)</Label>
                    <Input 
                      placeholder="Spanish, French" 
                      value={requestForm.languages}
                      onChange={(e) => setRequestForm({...requestForm, languages: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input 
                      type="date" 
                      value={requestForm.date}
                      onChange={(e) => setRequestForm({...requestForm, date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Input 
                      type="time" 
                      value={requestForm.time}
                      onChange={(e) => setRequestForm({...requestForm, time: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Duration (hours)</Label>
                    <Input 
                      type="number" 
                      min="0.5" 
                      step="0.5"
                      value={requestForm.duration}
                      onChange={(e) => setRequestForm({...requestForm, duration: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location / Meeting Link</Label>
                    <Input 
                      placeholder="Address or video link" 
                      value={requestForm.location}
                      onChange={(e) => setRequestForm({...requestForm, location: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea 
                    placeholder="Any special requirements or instructions..."
                    value={requestForm.notes}
                    onChange={(e) => setRequestForm({...requestForm, notes: e.target.value})}
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={loading}>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowRequestForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="orders-tab">
              <FileText className="h-4 w-4" />
              Work Orders
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="invoices-tab">
              <Receipt className="h-4 w-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="documents-tab">
              <FolderOpen className="h-4 w-4" />
              Documents
              {totalDocs > 0 && (
                <Badge className="ml-1 bg-purple-100 text-purple-800">{totalDocs}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Work Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Your Work Orders
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {workOrders.length > 0 ? (
                  <div className="space-y-3">
                    {workOrders.map((wo) => (
                      <div key={wo.work_order_id} className="border rounded-lg p-4 hover:bg-gray-50" data-testid={`work-order-${wo.work_order_id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-blue-600 text-white">{wo.work_order_id}</Badge>
                              {getStatusBadge(wo.status)}
                              <Badge variant="outline">{wo.work_order_type}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Languages className="h-4 w-4" />
                                {wo.languages?.join(', ')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {wo.interpreting_details?.start_datetime ? 
                                  formatDate(wo.interpreting_details.start_datetime) : 
                                  (wo.deadline ? formatDate(wo.deadline) : 'TBD')}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openUploadDialog(wo)}
                              data-testid={`upload-to-${wo.work_order_id}`}
                            >
                              <Paperclip className="h-4 w-4 mr-1" />
                              Attach File
                            </Button>
                            {wo.billed_amount > 0 && (
                              <p className="font-semibold">${wo.billed_amount?.toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No work orders yet</p>
                    <Button className="mt-4 bg-orange-500 hover:bg-orange-600" onClick={() => setShowRequestForm(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Request Your First Service
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-green-600" />
                  Your Invoices
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {invoices.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.map((inv) => (
                      <div key={inv.invoice_id} className="border rounded-lg p-4 flex items-center justify-between" data-testid={`invoice-${inv.invoice_id}`}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{inv.invoice_number}</span>
                            {getStatusBadge(inv.status)}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Due: {formatDate(inv.due_date)}
                            {inv.work_order_id && ` • ${inv.work_order_id}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="font-bold text-lg">${inv.total?.toLocaleString()}</p>
                          <Button variant="outline" size="sm" data-testid={`download-invoice-${inv.invoice_id}`}>
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No invoices yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="space-y-6">
              {/* Account Documents */}
              <Card>
                <CardHeader className="bg-purple-50">
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="h-5 w-5 text-purple-600" />
                    Account Documents
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {documents.client_documents.length > 0 ? (
                    <div className="space-y-2">
                      {documents.client_documents.map((doc) => (
                        <div key={doc.file_id} className="border rounded-lg p-3 flex items-center justify-between hover:bg-gray-50" data-testid={`doc-${doc.file_id}`}>
                          <div className="flex items-center gap-3">
                            <File className={`h-8 w-8 ${getFileIcon(doc.filename)}`} />
                            <div>
                              <p className="font-medium">{doc.filename}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{formatFileSize(doc.file_size)}</span>
                                {doc.current_version > 1 && (
                                  <Badge variant="outline" className="text-xs">v{doc.current_version}</Badge>
                                )}
                                <span>• {formatDate(doc.uploaded_at)}</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" asChild>
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No account documents</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Work Order Documents */}
              <Card>
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Work Order Documents
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {documents.work_order_documents.length > 0 ? (
                    <div className="space-y-2">
                      {documents.work_order_documents.map((doc) => (
                        <div key={doc.file_id} className="border rounded-lg p-3 flex items-center justify-between hover:bg-gray-50" data-testid={`wo-doc-${doc.file_id}`}>
                          <div className="flex items-center gap-3">
                            <File className={`h-8 w-8 ${getFileIcon(doc.filename)}`} />
                            <div>
                              <p className="font-medium">{doc.filename}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Badge variant="outline" className="text-xs">{doc.entity_id}</Badge>
                                <span>{formatFileSize(doc.file_size)}</span>
                                {doc.current_version > 1 && (
                                  <Badge variant="outline" className="text-xs">v{doc.current_version}</Badge>
                                )}
                                <span>• {formatDate(doc.uploaded_at)}</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" asChild>
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 mr-1" />
                              View
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No work order documents</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8">
        <div className="max-w-6xl mx-auto px-4 py-4 text-center text-sm text-muted-foreground">
          <p>Need help? Contact us at info@bylyngo.com or (414) 326-9565</p>
          <p className="mt-1">© {new Date().getFullYear()} Bylyngo Interpreting & Translation</p>
        </div>
      </footer>

      {/* File Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Upload Document
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedWorkOrderForUpload ? (
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Uploading to:</p>
                <p className="font-semibold text-blue-700">{selectedWorkOrderForUpload.work_order_id}</p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Uploading to your account documents</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Document Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(v) => setUploadForm({...uploadForm, category: v})}
              >
                <SelectTrigger data-testid="upload-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="source">Source Document</SelectItem>
                  <SelectItem value="instruction">Instructions</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})}
                placeholder="Any notes about this document..."
                rows={2}
                data-testid="upload-notes"
              />
            </div>

            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                data-testid="file-input"
              />
              {uploadingFile ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                  <p className="text-sm text-muted-foreground">Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">
                    PDF, Word, Excel, Images up to 10MB
                  </p>
                  <Button 
                    type="button" 
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="select-file-btn"
                  >
                    Select File
                  </Button>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortal;
